/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import { globalEnv } from '../../../../config/baseConfig';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { HeaderItem, sortingOrder } from '../Components/common';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../../config/axiosInstance';
import ParentModal from '../../../Utils/Components/parentModal';
import { AggregatedUninstallReasonsModalContent } from './aggregatedUninstallReasonsModal';

export interface IUninstallReason {
	deviceId: string;
	createdAt: Date;
	uninstallReasonDetails: string;
}

export default function AggregatedUninstalls({
	extensionId
}: {
	extensionId?: string;
}) {
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('createdAt');
	const [uninstallCount, setUninstallCount] = useState(0);

	const [isShowingUninstallReasonsModal, setIsShowingUninstallReasonsModal] =
		useState(false);
	const [selectedUninstallReason, setSelectedUninstallReason] = useState<
		{ reason: string; detailedReasons?: IUninstallReason[] } | undefined
	>(undefined);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setSelectedSection('Aggregated Uninstalls'));
	}, []);

	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isError, isFetching, isLoading } = useQuery(
		[selectedHeader, sortOrder, searchQuery],
		() => fetchUserUninstalls(),
		{
			cacheTime: 0,
			keepPreviousData: false,
			staleTime: 5000,
			refetchOnWindowFocus: false
		}
	);

	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchUserUninstalls() {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		let extensionsUrl = '';
		if (extensionId != null) extensionsUrl = `?extension=${extensionId}&`;

		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL + `/admin/user/uninstall-aggregated${extensionsUrl}`,
			config
		);

		if (data.totalDeviceIds != null) {
			setUninstallCount(data.totalDeviceIds);
		}

		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Reason"
					slugName="reason"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Unique device id count / percentage"
					slugName="deviceId"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="detailed Feedback"
					slugName="detailedReasons"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">
						{`Count: ${uninstallCount}`}
					</div>
				</th>
			</tr>
		);
	};

	return (
		<section>
			{selectedUninstallReason != null && (
				<ParentModal
					visible={isShowingUninstallReasonsModal}
					setModalOpen={setIsShowingUninstallReasonsModal}
					isChanged={false}
					setIsChanged={() => {
						return;
					}}
					isWarnModalOpen={false}
					setIsWarnModalOpen={() => {
						return;
					}}
				>
					<AggregatedUninstallReasonsModalContent
						reason={selectedUninstallReason.reason}
						detailedReasons={selectedUninstallReason.detailedReasons}
						extensionId={extensionId}
					/>
				</ParentModal>
			)}
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : data.uninstallReasons.length > 0 ? (
								<thead className="text-xs font-bold uppercase dark:text-primary-text-dark text-primary-text-light">
									<TableHeaders />
								</thead>
							) : (
								<></>
							)}
							{isFetching || isLoading || isError ? (
								<></>
							) : data.uninstallReasons.length > 0 ? (
								<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
									{data.uninstallReasons.length > 0 &&
										data.uninstallReasons.map((item: any) => (
											<TableData
												key={item.uninstallReason}
												reason={item.uninstallReason}
												detailedReasons={item.uninstallData}
												deviceIdCount={item.deviceIds}
												percentage={
													((item.deviceIds / uninstallCount) * 100).toFixed(2) +
													'%'
												}
												setIsShowingUninstallReasonsModal={
													setIsShowingUninstallReasonsModal
												}
												setSelectedUninstallReason={setSelectedUninstallReason}
											/>
										))}
								</tbody>
							) : (
								<div className="h-full ml-0 ">
									<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
										<div className="mx-auto my-auto self-center">
											<div className="min-h-16">
												<DataNotFoundAnimation
													height={dimensions.height / 1.5}
												/>
											</div>
											<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
												{"Couldn't find the data you're looking for!"}
											</h3>
										</div>
									</div>
								</div>
							)}
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : (
					isError && (
						<div className="h-full ml-0 ">
							<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
								<div className="mx-auto my-auto self-center">
									<div className="min-h-16">
										<DataNotFoundAnimation height={dimensions.height / 1.5} />
									</div>
									<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
										{"Couldn't find the data you're looking for!"}
									</h3>
								</div>
							</div>
						</div>
					)
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	reason: string;
	detailedReasons: IUninstallReason[];
	deviceIdCount: string;
	percentage: string;
	setIsShowingUninstallReasonsModal: (
		isShowingUninstallReasonsModal: boolean
	) => void;
	setSelectedUninstallReason: (selectedUninstallReason: {
		reason: string;
		detailedReasons: IUninstallReason[];
	}) => void;
};

const TableData = ({
	reason,
	deviceIdCount,
	percentage,
	detailedReasons,
	setIsShowingUninstallReasonsModal,
	setSelectedUninstallReason
}: tableDateType) => {
	const filteredReasons: IUninstallReason[] = detailedReasons.filter(
		(reason) => reason.uninstallReasonDetails
	);

	function randomInt(min: number, max: number) {
		min = Math.ceil(min);
		max = Math.floor(max);
		return Math.floor(Math.random() * (max - min + 1) + min); // The maximum is inclusive and the minimum is inclusive
	}

	return (
		<tr
			className=" cursor-pointer"
			onClick={() => {
				setSelectedUninstallReason({
					reason: reason,
					detailedReasons: filteredReasons
				});
				setIsShowingUninstallReasonsModal(true);
			}}
		>
			<td className={`w-3/12 md:w-6/12 p-2 break-all`}>
				<div className="text-left dark:text-secondary-text-dark text-secondary-text-light font-medium">
					{reason}
				</div>
			</td>

			<td className={`w-3/12 md:w-3/12 p-2 break-all`}>
				<div className="text-left dark:text-secondary-text-dark text-secondary-text-light font-medium">
					{`${deviceIdCount} ~ ~ ${percentage}`}
				</div>
			</td>
			<td className="md:w-3/12 p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
					{filteredReasons[randomInt(0, filteredReasons.length - 1)] != null
						? filteredReasons[randomInt(0, filteredReasons.length - 1)]
								.uninstallReasonDetails
						: ''}
				</div>
			</td>
		</tr>
	);
};
